import * as React from 'react';
import type { CurrencyCode } from '@insio/shared-types';
import { Input, InputProps } from './Input';
import { useCurrencyPrecision } from '../../hooks/decimal-hooks';
import { useRoundOnDebounce } from '../../hooks/use-round-on-debounce';

export interface DecimalInputProps extends Omit<InputProps, 'type' | 'value' | 'onChange'> {
  /** Current value. Numbers/strings are stringified, `null`/`undefined` render as empty. */
  value: string | number | null | undefined;
  /** Called with the raw input string — caller decides how to parse it. */
  onChange: (value: string) => void;
  /**
   * ISO 4217 currency code. When provided, `step` matches the currency's
   * decimal places, the displayed value is rounded and padded to that
   * precision while unfocused, and blurring renormalises the committed value
   * to the same precision via `roundToCurrencyPrecision`.
   */
  currencyCode?: CurrencyCode | null;
}

/**
 * `<input type="number">` wrapper that coerces nullable numeric values to the
 * input's string representation and forwards the raw string back on change.
 * Optional `currencyCode` routes formatting through `useCurrencyPrecision`,
 * giving decimal-aware `step`, blur-time rounding, and unfocused padding.
 */
export const DecimalInput = React.forwardRef<HTMLInputElement, DecimalInputProps>(
  ({ value, onChange, onBlur, onFocus, currencyCode, step, ...props }, ref) => {
    const { decimalPlaces, format } = useCurrencyPrecision(currencyCode);

    const [focused, setFocused] = React.useState(false);

    const rawValue = value !== undefined && value !== null ? String(value) : '';

    const displayValue = (() => {
      if (rawValue === '' || focused) return rawValue;
      return format(rawValue) ?? rawValue;
    })();

    const resolvedStep =
      step ?? (decimalPlaces != null ? (10 ** -decimalPlaces).toFixed(decimalPlaces) : undefined);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      const formatted = format(rawValue);
      if (formatted != null && formatted !== rawValue) onChange(formatted);
      onBlur?.(e);
    };

    // 500 ms after the last keystroke, drop any excess fractional digits.
    useRoundOnDebounce({
      rawValue,
      decimalPlaces,
      format,
      onChange,
      enabled: focused
    });

    return (
      <Input
        {...props}
        ref={ref}
        type='number'
        inputMode='decimal'
        value={displayValue}
        step={resolvedStep}
        onChange={e => onChange(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    );
  }
);

DecimalInput.displayName = 'DecimalInput';
